import { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';

function Pendulum() {
  const rodRef = useRef<THREE.Mesh>(null);
  const sphereRef = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    const time = clock.elapsedTime;
    const angle = Math.sin(time * 1.5) * (35 * Math.PI / 180); // ±35 degrees
    const rodLength = 2.5;

    if (rodRef.current && sphereRef.current) {
      // Update rod rotation
      rodRef.current.rotation.z = angle;

      // Position sphere at end of rod
      const x = Math.sin(angle) * rodLength;
      const y = -Math.cos(angle) * rodLength;
      sphereRef.current.position.set(x, y, 0);
    }
  });

  return (
    <group position={[0, 1, 0]}>
      {/* Anchor point */}
      <mesh position={[0, 0.1, 0]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshBasicMaterial color="#ef4444" />
      </mesh>
      {/* Rod */}
      <mesh ref={rodRef} position={[0, -1.25, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 2.5, 8]} />
        <meshBasicMaterial color="#ef4444" transparent opacity={0.4} />
      </mesh>
      {/* Bob */}
      <mesh ref={sphereRef} position={[0, -2.5, 0]}>
        <sphereGeometry args={[0.3, 32, 32]} />
        <meshStandardMaterial
          color="#ef4444"
          emissive="#ef4444"
          emissiveIntensity={0.8}
          metalness={0.3}
          roughness={0.4}
        />
      </mesh>
    </group>
  );
}

function SineWave() {
  const pointsRef = useRef<THREE.Points>(null);
  const particleCount = 100;
  const width = 4;
  const amplitude = 0.3;
  const wavelength = 1.5;

  useFrame(({ clock }) => {
    const time = clock.elapsedTime;
    const positions = pointsRef.current?.geometry.attributes.position;

    if (positions) {
      for (let i = 0; i < particleCount; i++) {
        const x = (i / particleCount) * width - width / 2;
        const phase = (time * 2) + (x / wavelength) * Math.PI * 2;
        const y = Math.sin(phase) * amplitude;
        positions.setY(i, y - 2.5);
        positions.setZ(i, 0);
      }
      positions.needsUpdate = true;
    }
  });

  const positions = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (i / particleCount) * width - width / 2;
    positions[i * 3 + 1] = 0;
    positions[i * 3 + 2] = 0;
  }

  return (
    <points ref={pointsRef} position={[0, 0, 0]}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial color="#ef4444" size={0.08} transparent opacity={0.8} />
    </points>
  );
}

function ForceVectorArrow() {
  const groupRef = useRef<THREE.Group>(null);

  return (
    <group ref={groupRef} position={[1.5, -3, 0]}>
      {/* Arrow shaft */}
      <mesh position={[0.6, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 1.2, 8]} />
        <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
      </mesh>
      {/* Arrow head */}
      <mesh position={[1.25, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[0.1, 0.25, 8]} />
        <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
      </mesh>
      {/* Label */}
      <Text
        position={[0.6, 0.4, 0]}
        fontSize={0.25}
        color="#ef4444"
        anchorX="center"
        anchorY="middle"
        font="https://fonts.gstatic.com/s/spacemono/v12/i7dPIFZifjKcF5UAWdDRYEF8RQ.woff2"
      >
        F = ma
      </Text>
    </group>
  );
}

function Scene() {
  return (
    <>
      <ambientLight color="#1a0000" intensity={0.5} />
      <pointLight position={[0, 3, 2]} color="#ef4444" intensity={2} />
      <pointLight position={[2, -2, 2]} color="#ef4444" intensity={1} />
      <Pendulum />
      <SineWave />
      <ForceVectorArrow />
    </>
  );
}

export default function PhysicsAnimation() {
  return (
    <Canvas
      orthographic
      camera={{ zoom: 50, position: [0, -0.5, 10], near: 0.1, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'transparent' }}
    >
      <Scene />
    </Canvas>
  );
}